import React, { useEffect, useMemo, useState } from "react";
import RailGroup from "./RailGroup";
import PersonCard from "./PersonCard";
import { firstName } from "./format.mjs";
import { peopleList, duplicateLines, referrerOf, peopleSummary } from "./railModel.mjs";

/*
 * PEOPLE (frames 1a, 1b, 1d; summaries in 1c and 1g): everyone at the company,
 * visible, the point of contact marked and changeable (the brief: "not behind a
 * 'history & contacts' link").
 *
 * The point of contact's card is open by default, and so is the person whose
 * record this is when they are not the point of contact (the matrix's Associated
 * cell: "Their place at the company"); everyone else is one row that opens in
 * place. Opening someone's whole record is "Open →" on their card, in the same
 * panel with "← {company}" back (the shell's person stack).
 *
 * THE ONE RED BOX is a suspected duplicate, printed from its STORED reason
 * (reality.txt [22]: "Shachar Eschel has two records: same name, two emails.
 * Flagged Sep 16."), and the pair shows as one row flagged "2 records". Merge is
 * Proposed: an inert tag, nothing a click can reach.
 *
 * THE REFERRER is an outsider (Dani has no company on his record), shown under the
 * company's people and never counted with them (reality.txt [10]). Showing an
 * outsider without moving them is Proposed; "Link to {company}" is real, moves
 * them into the company with a confirm, and lives here because this is where the
 * question is asked.
 *
 * Roles on the deal (signer, legal, champion) are not stored anywhere: at
 * Agreement + legal, where the matrix asks for them, they are one Proposed line,
 * never a role printed beside a name as if we knew it.
 *
 * Props:
 *   record   the shell's bundle: company (contacts, primary_contact_id, duplicates),
 *            contact, id (the record on screen), key (the situation), timeline.rows
 *   ops      ops.patchCompany({ primary_contact_id }), ops.patchContact(id, body),
 *            ops.call(path, options), ops.reload, ops.openPerson(id)
 *   state    "open" | "summary": the matrix cell
 *   sheet    boolean, phone: opens as a bottom sheet
 *   line     string, the matrix cell's words ("One person · Add a person")
 */

const EMPTY_FORM = { first_name: "", last_name: "", email: "", job_title: "" };

function Referrer({ referrer, company, ops }) {
  // Whether they have a company of their own decides what "Link to Pluto" does to
  // them, and the record timeline names only the sender, so their own record is
  // read once to say it.
  const [theirs, setTheirs] = useState(undefined);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    let live = true;
    ops
      .call(`/contacts/${referrer.contact_id}`)
      .then((c) => live && setTheirs(c && c.company ? c.company.name : null))
      .catch(() => live && setTheirs(undefined));
    return () => {
      live = false;
    };
    // The referrer is the record's, so a reload that keeps them keeps the answer.
    // (No eslint-disable here: the build runs CRA's base config, which has no
    // react-hooks plugin, and naming its rule fails the build.)
  }, [referrer.contact_id]);

  const link = async () => {
    setBusy(true);
    setError(null);
    try {
      await ops.patchContact(referrer.contact_id, { company_id: company.id });
      setConfirming(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const who = referrer.first || referrer.name || "the referrer";
  const where = theirs === null ? `no company on ${who}’s record` : theirs ? `at ${theirs}` : null;
  return (
    <>
      <div className="rec-personrow rec-personrow--static">
        <span className="rec-strong">{referrer.name}</span>
        <span className="rec-personrow__title">Referrer{where ? ` · ${where}` : ""}</span>
        <span className="rec-proposed">Proposed</span>
        <span className="rec-personrow__end">
          <button type="button" className="rec-link rec-link--plain" onClick={() => ops.openPerson(referrer.contact_id)}>Open →</button>
        </span>
      </div>
      <div className="rec-referrer__note">
        {confirming ? (
          <span className="rec-stack">
            <span>
              Move {who} into {company.name}?{theirs ? ` They leave ${theirs}.` : ""} Everything on {who}’s record comes along to {company.name}’s timeline.
            </span>
            <span className="rec-actionsrow">
              <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={busy} onClick={link}>Move</button>
              <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
              {error && <span className="rec-err">{error}</span>}
            </span>
          </span>
        ) : (
          <>
            Shown without moving them: only their {company.name} threads would come along.{" "}
            <button type="button" className="rec-link rec-link--body" onClick={() => setConfirming(true)}>Link to {company.name}</button> moves them.
          </>
        )}
      </div>
    </>
  );
}

export default function People({ record, ops, state = "summary", sheet = false, line = "" }) {
  const company = record.company;
  const contact = record.contact;
  const list = useMemo(() => peopleList({ company, contact, recordId: record.id }), [company, contact, record.id]);
  const dupes = useMemo(() => duplicateLines(company, { now: record.now }), [company, record.now]);
  const referrer = useMemo(() => (company ? referrerOf({ timeline: record.timeline.rows, company }) : null), [company, record.timeline.rows]);
  const associated = record.key === "associated";
  const pocId = list.poc ? String(list.poc.id) : null;

  const [openIds, setOpenIds] = useState(() => {
    const s = new Set();
    if (pocId) s.add(pocId);
    if (!pocId || associated || !company) s.add(String(record.id));
    return s;
  });
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (id) =>
    setOpenIds((s) => {
      const next = new Set(s);
      const k = String(id);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim() || undefined]));
      await ops.call(`/companies/${company.id}/contacts`, { method: "POST", body: JSON.stringify(body) });
      setForm(EMPTY_FORM);
      setAdding(false);
      await ops.reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const makePoc = async (id) => {
    setBusy(true);
    setError(null);
    try {
      await ops.patchCompany({ primary_contact_id: id });
      setChoosing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // "Make point of contact" names who it will make when there is one obvious
  // colleague (the one opened, or the only other person), and asks who otherwise.
  const candidates = list.people.filter((x) => !x.isPoc);
  const openCandidates = candidates.filter((x) => openIds.has(String(x.person.id)));
  const direct = openCandidates.length === 1 ? openCandidates[0].person : candidates.length === 1 ? candidates[0].person : null;
  const summary = peopleSummary({ list, contact, company, dupes: dupes.length, line, associated });
  const rolesProposed = /Roles on the deal/.test(String(line || ""));

  return (
    <RailGroup id="people" title="People" count={company ? list.count : null} summary={summary} defaultOpen={state === "open"} sheet={sheet}>
      {dupes.map((d) => (
        <div className="rec-dupe" key={d.key} role="alert">
          <span className="rec-dupe__dot" aria-hidden="true" />
          <div style={{ flex: 1 }}>
            <b className="rec-strong">{d.head}</b>
            {d.text}{" "}
            <span className="rec-proposed-wrap">
              <span className="rec-inert">Merge</span>
              <span className="rec-proposed">Proposed</span>
            </span>
          </div>
        </div>
      ))}

      <div className="rec-people">
        {list.people.map(({ person, isPoc, dupe }) => (
          <PersonCard
            key={person.id}
            person={person}
            record={record}
            ops={ops}
            isPoc={isPoc}
            dupe={dupe}
            open={openIds.has(String(person.id))}
            onToggle={() => toggle(person.id)}
          />
        ))}
        {referrer && company && <Referrer referrer={referrer} company={company} ops={ops} />}
      </div>

      {rolesProposed && (
        <div className="rec-row__proposed">
          A role on this deal for each person: signer, legal, billing, champion
          <span className="rec-proposed">Proposed</span>
        </div>
      )}

      {adding && company && (
        <div className="rec-dealedit">
          <div className="rec-editrow">
            <input className="rec-input" autoFocus aria-label="First name" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            <input className="rec-input" aria-label="Last name" placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <div className="rec-editrow">
            <input className="rec-input" inputMode="email" aria-label="Email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input className="rec-input" aria-label="Title" placeholder="Title" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} />
          </div>
          <span className="rec-field__hint">Added as associated: a colleague on the account, not a new lead.</span>
          <div className="rec-actionsrow">
            <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={busy || (!form.email.trim() && !form.first_name.trim() && !form.last_name.trim())} onClick={add}>
              {busy ? "Adding…" : "Add"}
            </button>
            <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" disabled={busy} onClick={() => { setAdding(false); setError(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {choosing && (
        <div className="rec-stack">
          <span className="rec-field__label">Who is the point of contact?</span>
          <div className="rec-chips">
            {candidates.map(({ person }) => (
              <button key={person.id} type="button" className="rec-chip" disabled={busy} onClick={() => makePoc(person.id)}>
                {firstName(person)}
              </button>
            ))}
            <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" disabled={busy} onClick={() => setChoosing(false)}>Cancel</button>
          </div>
        </div>
      )}

      {error && <span className="rec-err">{error}</span>}

      {company && !adding && !choosing && (
        <div className="rec-people__foot">
          <button type="button" className="rec-link" onClick={() => { setAdding(true); setError(null); }}>Add a person</button>
          {candidates.length > 0 && (
            <button
              type="button"
              className="rec-link"
              disabled={busy}
              onClick={() => (direct ? makePoc(direct.id) : setChoosing(true))}
            >
              {direct ? `Make ${firstName(direct)} point of contact` : "Make point of contact"}
            </button>
          )}
        </div>
      )}
    </RailGroup>
  );
}
